import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import SimpleHeader from '../layouts/SimpleHeader'
import LoadingPage from './LoadingPage'
import Table from '../components/ReactTable/Table'
import { COLUMNS, ORDER } from '../components/ReactTable/columns'
import { useQuery } from '@apollo/client'
import { MY_CAMPAIGNS_QUERY } from '../services/GraphQLQueries'
import SideMenu from '../layouts/SideMenu'
import MenuWrap from '../components/BurgerMenu/MenuWrap'
import { do_download_string } from '../services/downloads'
import '../layouts/stylesheets/trackers-style.css'
import { isMobile } from '../components/DeviceDetection'
import Modal from '../layouts/Modal'

const Trackers = () => {
  const [campaigns, setCampaigns] = useState([]) // ✅ local state
  const [isModalOpen, setIsModalOpen] = useState(isMobile())
  const navigate = useNavigate()

  const formatPlatform = (platform) => {
    if (!platform || typeof platform !== 'string') {
      return 'Unknown'
    }

    return platform.charAt(0).toUpperCase() + platform.slice(1)
  }

   // ✅ Check login status immediately
  useEffect(() => {
    const token = sessionStorage.getItem('ontrack-auth-token') || localStorage.getItem('ontrack-auth-token')
    if (!token) {
      console.warn('[Auth] No login token found — redirecting to signin')
      navigate('/signin', { replace: true })
      return
    }
  }, [navigate])

  // === GraphQL Query ===
  const { data, loading } = useQuery(MY_CAMPAIGNS_QUERY, {
    fetchPolicy: 'no-cache',
    variables: { length: 0 },
  })

  useEffect(() => {
    if (!data?.myCampaigns?.campaignDatas) return
    const campaignsData = data.myCampaigns.campaignDatas.map((campaign) => ({
      ...campaign,
      platform: formatPlatform(campaign.platform),
      campaignType: campaign.campaignType || 'Unknown',
      campaignName: campaign.campaignName || 'Untitled campaign',
      createdAt: campaign.createdAt
        ? new Date(campaign.createdAt).toLocaleDateString('en-US')
        : '',
    }))
    setCampaigns(campaignsData)
    if (campaignsData.length < 1) navigate('/first-tracker')
  }, [data, navigate])

  // === Row Removal ===
  const removeData = (id) => {
    setCampaigns((prev) => prev.filter((t) => t.id !== id))
  }

  // === CSV Export ===
  const generate_formatted_content = (data) => {
    return [
      [
        'Campaign Type',
        'Campaign Name',
        'Created At',
        'Clicks',
        'Bots',
        'CPA',
        'Conversions',
      ],
    ].concat(
      data.map((r) => [
        `${r.platform || 'Unknown'} / ${r.campaignType || 'Unknown'}`,
        r.campaignName || 'Untitled campaign',
        r.createdAt || '',
        r.clicks || 0,
        r.botClicks || 0,
        r.conversions > 0 ? (r.campaignBudget || 0) / r.conversions : 0,
        r.conversions || 0,
      ])
    )
  }

  return loading ? (
    <LoadingPage />
  ) : (
    <>
      <div id="outer-container" style={{ height: '100%' }}>
        <MenuWrap className="sm:hidden">
          <SideMenu isAuthorized={true}></SideMenu>
        </MenuWrap>
        <div id="page-wrap">
          <div className="flex">
            <SideMenu
              isAuthorized={true}
              className="hidden sm:block"
            ></SideMenu>
            <div className="min-w-max w-full">
              <SimpleHeader
                beforeDownloadAction={
                  <button
                    type="button"
                    onClick={() =>
                      navigate('/new-tracker', {
                        state: { activePage: 'source', pageType: 'newTracker' },
                      })
                    }
                    className="mr-4 bg-[#111111] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white"
                  >
                    Create M Link
                  </button>
                }
                downloadAction={() =>
                  do_download_string({
                    content: generate_formatted_content(campaigns),
                    filename: 'trackers.csv',
                  })
                }
              />
              <div className="h-[calc(90%)]" aria-live="polite">
                <div className="relative mx-auto mb-12 pt-6">
                  <h3 className="col-span-11 w-full text-4xl text-center font-unbounded">
                    Campaigns Overview
                  </h3>
                </div>
                <div>
                  <Table
                    columns={COLUMNS}
                    data={campaigns}
                    order={ORDER}
                    dashboardActive={true}
                    columnHiding={true}
                    height={75}
                    tableClass="h-[calc(61vh)] py-4"
                    topOffset={25}
                    width={{ width: 1600, tailwindWidth: 'w-[1600px]' }}
                    removeData={removeData}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isModalOpen && (
        <Modal setIsModalOpen={setIsModalOpen} backGroundClass={'darkBG'}>
          <div className="modalContent mx-auto bg-onTrack-light-pink rounded p-3">
            <p className={`text-red-600 ${isModalOpen}`}>
              This application should be accessed on a desktop for optimal
              functionality and user experience, as it is not intended for
              mobile use.
            </p>
          </div>
        </Modal>
      )}
    </>
  )
}

export default Trackers
